import type { Element, Root, Text } from 'hast';
import type { AdvisorySourceQuote, AdvisorySourceReferenceV01 } from './advisoryProvenance';

/** UI identity of one saved quote, not a producer segment/character offset. */
export interface AdvisoryQuoteReference {
  key: string;
  label: string;
  source: AdvisorySourceReferenceV01;
  quote: AdvisorySourceQuote;
  quoteIndex: number;
}

export interface AdvisoryQuoteSelection {
  sourceKey: string;
  quoteIndex?: number;
}

interface QuoteAnchor {
  start: number;
  end: number;
  reference: AdvisoryQuoteReference;
}

function normalizeWhitespace(text: string) {
  let value = '';
  const starts: number[] = [];
  const ends: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    const char = /\s/u.test(text[index]) ? ' ' : text[index];
    if (char === ' ' && value.endsWith(' ')) {
      ends[ends.length - 1] = index + 1;
    } else {
      value += char;
      starts.push(index);
      ends.push(index + 1);
    }
  }
  return { value, starts, ends };
}

interface MatchableQuote {
  reference: AdvisoryQuoteReference;
  quote: string;
}

/**
 * Normalize and screen each recorded quote once per references array rather than
 * once per rendered value. The Brief renders hundreds of values per chapter and
 * reads its references from a memoized context, so the array identity is stable.
 * Nothing mutates these arrays in place; a new array simply repeats the work.
 */
const matchableQuotes = new WeakMap<readonly AdvisoryQuoteReference[], MatchableQuote[]>();

function toMatchableQuotes(references: readonly AdvisoryQuoteReference[]): MatchableQuote[] {
  const cached = matchableQuotes.get(references);
  if (cached) return cached;
  const prepared = references.flatMap(reference => {
    const quote = normalizeWhitespace(reference.quote.text).value.trim();
    return quote.length < 16 || (quote.match(/\p{L}+/gu)?.length ?? 0) < 3
      ? []
      : [{ reference, quote }];
  });
  matchableQuotes.set(references, prepared);
  return prepared;
}

/**
 * Conservative bridge for answers without explicit inline citation bindings.
 * Match a full recorded phrase, case/punctuation/numbers intact. Whitespace is
 * the only normalization. Bare figures and short labels cannot establish a link.
 * Keep every matching reference, including disagreeing checks on the same text.
 */
export function findAdvisoryQuoteAnchors(
  text: string,
  references: readonly AdvisoryQuoteReference[]
): QuoteAnchor[] {
  if (!text || references.length === 0) return [];
  const normalized = normalizeWhitespace(text);
  const anchors: QuoteAnchor[] = [];
  for (const { reference, quote } of toMatchableQuotes(references)) {
    let cursor = 0;
    while (cursor < normalized.value.length) {
      const start = normalized.value.indexOf(quote, cursor);
      if (start < 0) break;
      const end = start + quote.length;
      const cutsWord = (left: string, right: string) =>
        /[\p{L}\p{N}]/u.test(left) && /[\p{L}\p{N}]/u.test(right);
      if (
        !cutsWord(normalized.value[start - 1] ?? '', quote[0]) &&
        !cutsWord(quote[quote.length - 1], normalized.value[end] ?? '')
      ) {
        let markerEnd = normalized.ends[end - 1];
        // Step past a closing mark that ends the quotation, never past an
        // apostrophe: `’` also spells possessives and contractions, and a marker
        // placed after it would split the word it belongs to.
        while (
          /[”’"»]/u.test(text[markerEnd] ?? '') &&
          !/[\p{L}\p{N}]/u.test(text[markerEnd + 1] ?? '')
        )
          markerEnd += 1;
        anchors.push({
          start: normalized.starts[start],
          end: markerEnd,
          reference,
        });
      }
      cursor = end;
    }
  }
  return anchors.sort((a, b) => a.end - b.end || a.start - b.start);
}

const inlineTags = new Set(['strong', 'em', 'del', 'span', 'sup', 'sub']);
const textBlocks = new Set(['p', 'li', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

/** Rehype transform: preserve Markdown formatting, never put buttons inside links/code. */
export function annotateAdvisoryMarkdownQuotes(
  tree: Root,
  references: readonly AdvisoryQuoteReference[]
): void {
  const visit = (parent: Root | Element) => {
    if (parent.type === 'element' && ['a', 'code', 'pre'].includes(parent.tagName)) return;
    // Descend first, so newly inserted markers are never interpreted as answer text.
    parent.children.forEach(child => {
      if (child.type === 'element') visit(child);
    });
    if (parent.type !== 'element' || !textBlocks.has(parent.tagName)) return;
    let text = '';
    const leaves: { node: Text; parent: Element; start: number }[] = [];
    const collect = (element: Element) => {
      element.children.forEach(child => {
        if (child.type === 'text') {
          leaves.push({ node: child, parent: element, start: text.length });
          text += child.value;
        } else if (child.type === 'element' && inlineTags.has(child.tagName)) {
          collect(child);
        } else if (child.type === 'element' && child.tagName === 'br') {
          text += '\n';
        } else {
          // A link, code span or nested block is a hard matching boundary.
          text += '\0';
        }
      });
    };
    collect(parent);
    const anchors = findAdvisoryQuoteAnchors(text, references);
    for (const leaf of leaves) {
      const local = anchors.filter(
        anchor => anchor.end > leaf.start && anchor.end <= leaf.start + leaf.node.value.length
      );
      if (!local.length) continue;
      const replacement: Element['children'] = [];
      let cursor = 0;
      for (const anchor of local) {
        const end = anchor.end - leaf.start;
        if (end > cursor)
          replacement.push({ type: 'text', value: leaf.node.value.slice(cursor, end) });
        replacement.push({
          type: 'element',
          tagName: 'sup',
          properties: { dataAdvisoryQuote: anchor.reference.key },
          children: [],
        });
        cursor = end;
      }
      if (cursor < leaf.node.value.length) {
        replacement.push({ type: 'text', value: leaf.node.value.slice(cursor) });
      }
      leaf.parent.children.splice(leaf.parent.children.indexOf(leaf.node), 1, ...replacement);
    }
  };
  visit(tree);
}
